import styled from 'styled-components';
import { Typography } from 'design-system/src/components/Typography/Typography';

// this is different enough from  InfoBox that it wouldn't save us much to inherit from it
const StyledSavingsProgramRequirements = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 200px;
  overflow-y: auto;
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--grayscale-100);
  background-color: var(--surface-100);
  padding: 16px;

  ul {
    padding-inline-start: 16px;
    margin: 0;
  }
`;

const SectionHeading = styled(Typography).attrs({
  styledAs: 'bodySpaceGrotesk',
  weight: 500,
})``;

const SectionContent = styled(Typography).attrs({
  styledAs: 'bodyExtraSmallSpaceGrotesk',
})``;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/**
 * Program requirements for the TREMFYA withMe Savings Program
 */
export const SavingsProgramRequirements = () => {
  return (
    <StyledSavingsProgramRequirements>
      <Section>
        <SectionHeading>Savings Program Requirements</SectionHeading>
        <SectionContent>
          The TREMFYA withMe Savings Program provides two support offerings for eligible patients: Medicine Cost Support
          and Treatment Administration Cost Support.
        </SectionContent>
        <ul>
          <SectionContent renderedAs="li">
            Medicine Cost Support can help eligible patients pay $0 per dose. Maximum program benefit per calendar year
            shall apply. Terms expire at the end of each calendar year. Offer subject to change or end without notice.
            Restrictions, including monthly maximums, may apply.
          </SectionContent>
          <SectionContent renderedAs="li">
            Treatment Administration Cost Support can help eligible patients save on out-of-pocket costs for:
            <ul>
              <SectionContent renderedAs="li">
                Required laboratory tests per the product prescribing information
              </SectionContent>
              <SectionContent renderedAs="li">Intravenous infusion of the medicine</SectionContent>
            </ul>
          </SectionContent>
        </ul>
        <SectionContent>
          There is a $2,000 maximum benefit per calendar year for Treatment Administration Cost Support.
        </SectionContent>
        <SectionContent>
          The patient or healthcare provider is required to submit an Explanation of Benefits (EOB) following each
          laboratory test or infusion to the TREMFYA withMe Savings Program. Terms expire at the end of each calendar
          year. Offer subject to change or end without notice, including in specific states. Not valid for residents of
          MA, MN, or RI.
        </SectionContent>
      </Section>

      <Section>
        <SectionHeading>Medicine Cost Support</SectionHeading>
        <SectionContent>Am I eligible for Medicine Cost Support?</SectionContent>
        <SectionContent>
          You may be eligible for the Medicine Cost Support through TREMFYA withMe Savings Program if you are age 18 or
          older, use commercial or private health insurance for your prescribed TREMFYA® (guselkumab), and must pay an
          out-of-pocket cost for your medicine. Participate without sharing your income information.
        </SectionContent>
        <SectionContent>
          Some health plans have programs or benefit designs known as “accumulators” or “maximizers.” These programs
          divert patient assistance funds away from patients.
        </SectionContent>
        <ul>
          <SectionContent renderedAs="li">
            Accumulators don't allow patient assistance to count toward the patient's deductible and out-of-pocket
            maximum until the maximum value of the patient assistance is reached.
          </SectionContent>
          <SectionContent renderedAs="li">
            Maximizers also don't allow patient assistance to count toward the patient's deductible and out-of-pocket
            maximum. Maximizers apply the full value of the patient assistance over the year. This could be either the
            same amount each month or a larger amount early in the year that tapers off, without allowing any of those
            funds to count toward the patient's annual deductible or out-of-pocket maximum.
          </SectionContent>
          <SectionContent renderedAs="li">
            The TREMFYA withMe Savings Program is designed solely for the benefit of the patient. Thus, Johnson &
            Johnson reserves the right to reduce the TREMFYA withMe Savings Program maximum benefit for patients in an
            accumulator or maximizer program or benefit design, except where prohibited by law.
          </SectionContent>
        </ul>
        <SectionContent>
          In addition, some health plans have “non-essential health benefit maximizers” that conflict with the program
          requirements of the TREMFYA withMe Savings Program.
        </SectionContent>
        <ul>
          <SectionContent renderedAs="li">
            These programs or benefit designs, like the services offered by SaveOnSP, classify certain specialty
            medicines such as TREMFYA® as “non-essential.” This takes away protections for patients provided by the
            Affordable Care Act (ACA) related to maximum out-of-pocket limits.
          </SectionContent>
          <SectionContent renderedAs="li">
            The TREMFYA withMe Savings Program is designed solely for the benefit of the patient. If your insurance
            company or health plan partners with SaveOnSP, then except where prohibited by law, you will not be eligible
            for, and you agree not to use, the TREMFYA withMe Savings Program.
          </SectionContent>
          <SectionContent renderedAs="li">
            Please let TREMFYA withMe know if your insurance company or health plan has one of these programs or benefit
            designs, including SaveOnSP, by calling 833-WITHME1 (833-948-4631) to discuss your options. Since you may
            not know you are subject to one of these programs or benefit designs when you enroll in TREMFYA withMe, J&J
            will monitor your utilization.
          </SectionContent>
          <SectionContent renderedAs="li">
            J&J reserves the right to discontinue cost support if you no longer meet eligibility requirements.
          </SectionContent>
          <SectionContent renderedAs="li">
            If your health plan removes TREMFYA® from its partnership with SaveOnSP or other non-essential health
            benefit maximizer, you may be eligible to be reinstated in the TREMFYA withMe Savings Program.
          </SectionContent>
        </ul>
        <SectionContent>
          By utilizing this Savings Program, you accept and agree to abide by these program requirements. Any individual
          or entity who enrolls or assists in the enrollment of a patient in the Savings Program represents that the
          patient meets the eligibility criteria and other requirements described.
        </SectionContent>
      </Section>

      <Section>
        <SectionHeading>Other requirements</SectionHeading>
        <ul>
          <SectionContent renderedAs="li">
            This program is only for people age 18 or older using commercial or private health insurance who must pay an
            out-of-pocket cost for their prescribed TREMFYA®. This includes plans from the Health Insurance Marketplace.
            This program is not for people who use any state or federal government-funded healthcare program. Examples
            of these programs are Medicare, Medicaid, TRICARE, Department of Defense, and Veterans Administration.
          </SectionContent>
          <SectionContent renderedAs="li">
            By enrolling in this program, you agree that this program is intended solely for the benefit of you, the
            patient. You may not seek payment for the value received from this program from any health plan, patient
            assistance foundation, flexible spending account, or healthcare savings account.
          </SectionContent>
          <SectionContent renderedAs="li">
            You must meet the program requirements every time you use the Savings Program.
          </SectionContent>
          <SectionContent renderedAs="li">
            Program terms will expire at the end of each calendar year. The program may change or end without notice,
            including in specific states.
          </SectionContent>
          <SectionContent renderedAs="li">
            Program participants are subject to an annual maximum benefit. Program benefits are set at the discretion of
            J&J and may change without notice.
          </SectionContent>
          <SectionContent renderedAs="li">
            Patients who are subject to programs, health plans, or benefits that claim to reduce their patients'
            out-of-pocket co-pay, co-insurance, or deductible obligations for certain prescription drugs based upon the
            availability of, or patient's enrollment in, manufacturer-sponsored co-pay assistance for such drugs will be
            subject to a reduced annual maximum program benefit per calendar year (not applicable to patients in Maine).
          </SectionContent>
          <SectionContent renderedAs="li">
            Patients who are subject to programs, health plans, or benefits that claim to eliminate their out-of-pocket
            costs are not eligible for the TREMFYA withMe Savings Program, because this program is only for people who
            must pay an out-of-pocket cost for TREMFYA®.
          </SectionContent>
          <SectionContent renderedAs="li">
            Notwithstanding any other term of this program, patients who are members of health plans that partner with
            SaveOnSP, or who are subject to services administered by SaveOnSP, are not eligible for the TREMFYA withMe
            Savings Program. If your health plan removes TREMFYA® from its partnership with SaveOnSP, you may be
            eligible for the TREMFYA withMe Savings Program.
          </SectionContent>
          <SectionContent renderedAs="li">
            To use this program, you must follow any health plan requirements, including telling your health plan how
            much co-payment support you get from this program, if required. By using the Savings Program, you confirm
            that you have read, understood, and agree to the program requirements on this page, and you are giving
            permission for information related to your Savings Program transactions to be shared with your healthcare
            provider(s). These transactions include rebates and any funds placed on the card or balance remaining on the
            card.
          </SectionContent>
          <SectionContent renderedAs="li">
            Before you enroll in the program, you will be asked to provide personal information that may include your
            name, address, phone number, email address, and/or other information, including information related to your
            prescription medicine insurance and treatment. This information is needed for Johnson & Johnson Health Care
            Systems Inc. and our service providers to enroll you in the TREMFYA withMe Savings Program. The use of your
            information will be governed by our Privacy Policy.
          </SectionContent>
          <SectionContent renderedAs="li">
            If you use medical/primary insurance to pay for your medicine, you need to submit a rebate request with an
            Explanation of Benefits (EOB) to get payment from the Savings Program. With your permission, your provider
            may submit the rebate request and EOB for you. Please make sure you and your provider know who will submit
            the rebate request. Rebate requests must be submitted within 365 days of the date of service.
          </SectionContent>
          <SectionContent renderedAs="li">
            This program offer may not be used with any other coupon, discount, prescription savings card, free trial,
            or other offer. Offer good only in the United States and its territories. Void where prohibited, taxed, or
            limited by law.
          </SectionContent>
        </ul>
        <SectionContent>
          You may end your participation in TREMFYA withMe at any time by calling{' '}
          <strong>833-WITHME1 (833-948-4631).</strong>
        </SectionContent>
      </Section>

      <Section>
        <SectionHeading>Treatment Administration Cost Support</SectionHeading>
        <SectionContent>Am I eligible for Treatment Administration Cost Support?</SectionContent>
        <SectionContent>
          If you have been prescribed TREMFYA® infusion, you may be eligible for Treatment Administration Cost Support
          through the TREMFYA withMe Savings Program if you are age 18 or older, use commercial or private health
          insurance for TREMFYA®, and must pay an out-of-pocket cost for your TREMFYA® administration and eligible
          laboratory tests. Participate without sharing your income information.
        </SectionContent>
        <SectionContent>
          By utilizing this Savings Program, you accept and agree to abide by these program requirements. Any individual
          or entity who enrolls or assists in the enrollment of a patient in the Savings Program represents that the
          patient meets the eligibility criteria and other requirements described.
        </SectionContent>
      </Section>

      <Section>
        <SectionHeading>Other requirements</SectionHeading>
        <ul>
          <SectionContent renderedAs="li">
            <strong>
              This program is only for people age 18 or older using commercial or private health insurance who must pay
              an out-of-pocket cost for their TREMFYA® treatment. This includes plans from the Health Insurance
              Marketplace.
            </strong>{' '}
            This program is not for people who use any state or federal government-funded healthcare program. Examples
            of these programs are Medicare, Medicaid, TRICARE, Department of Defense, and Veterans Administration.
          </SectionContent>
          <SectionContent renderedAs="li">
            By enrolling in this program, you agree that this program is intended solely for the benefit of you, the
            patient. You may not seek payment for the value received from this program from any health plan, patient
            assistance foundation, flexible spending account, or healthcare savings account.
          </SectionContent>
          <SectionContent renderedAs="li">
            You must meet the program requirements every time you use the Savings Program.
          </SectionContent>
          <SectionContent renderedAs="li">
            Program terms will expire at the end of each calendar year. The program may change or end without notice,
            including in specific states. Treatment Administration Cost Support is not valid for residents of MA, MI,
            MN, or RI.
          </SectionContent>
          <SectionContent renderedAs="li">
            To use this Treatment Administration Cost Support, you must follow any health plan requirements, including
            telling your health plan how much co-payment support you get from this program, if required. By using the
            Savings Program, you confirm that you have read, understood, and agree to the program requirements on this
            page, and you are giving permission for information related to your Savings Program transactions to be
            shared with your healthcare provider(s). These transactions include rebates and any funds placed on the card
            or balance remaining on the card.
          </SectionContent>
          <SectionContent renderedAs="li">
            Before you enroll in the program, you will be asked to provide personal information that may include your
            name, address, phone number, email address, and/or other information, including information related to your
            prescription medicine insurance and treatment. This information is needed for Johnson & Johnson Health Care
            Systems Inc. and our service providers to enroll you in the TREMFYA withMe Savings Program. The use of your
            information will be governed by our Privacy Policy.
          </SectionContent>
          <SectionContent renderedAs="li">
            This program offer may not be used with any other coupon, discount, prescription savings card, free trial,
            or other offer. Offer good only in the United States and its territories, excluding states noted above. Void
            where prohibited, taxed, or limited by law.
          </SectionContent>
        </ul>
        <SectionContent>
          You may end your participation in TREMFYA withMe at any time by calling{' '}
          <strong>833-WITHME1 (833-948-4631).</strong>
        </SectionContent>
      </Section>
    </StyledSavingsProgramRequirements>
  );
};

export default SavingsProgramRequirements;
